import React, { useRef, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline, Link, List, ListOrdered, Heading2 } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Metin girin...',
  minHeight = '150px'
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  // Sadece dışarıdan gelen değişikliklerde içeriği güncelle
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      // Sadece focus yoksa veya içerik tamamen farklıysa güncelle
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const execCommand = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val);
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
    editorRef.current?.focus();
  }, [onChange]);

  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  const handleUnderline = () => execCommand('underline');

  const handleLink = () => {
    const url = prompt('Link URL girin:', 'https://');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleUnorderedList = () => execCommand('insertUnorderedList');
  const handleOrderedList = () => execCommand('insertOrderedList');

  const handleHeading = () => {
    execCommand('formatBlock', '<h3>');
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="border border-dark-600 rounded-lg overflow-hidden bg-dark-700/50">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-dark-600 bg-dark-800/50 flex-wrap">
        <button
          type="button"
          onClick={handleBold}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="Kalın (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleItalic}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="İtalik (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleUnderline}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="Altı Çizili (Ctrl+U)"
        >
          <Underline className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-dark-600 mx-1" />

        <button
          type="button"
          onClick={handleHeading}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="Başlık"
        >
          <Heading2 className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-dark-600 mx-1" />

        <button
          type="button"
          onClick={handleUnorderedList}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="Madde Listesi"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleOrderedList}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="Numaralı Liste"
        >
          <ListOrdered className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-dark-600 mx-1" />

        <button
          type="button"
          onClick={handleLink}
          className="p-2 hover:bg-dark-600 rounded text-gray-300 hover:text-white transition-colors"
          title="Link Ekle"
        >
          <Link className="w-4 h-4" />
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 text-white outline-none prose prose-invert max-w-none overflow-y-auto"
        style={{ minHeight }}
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #6b7280;
          pointer-events: none;
        }
        [contenteditable] h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.5rem 0;
        }
        [contenteditable] ul, [contenteditable] ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        [contenteditable] li {
          margin: 0.25rem 0;
        }
        [contenteditable] a {
          color: #60a5fa;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
